import './Banner.scss';
import left_gif from './img/left-gif.gif';
import right_gif from './img/right-gif.gif';
import { Heading } from '../Typography';
import { useCallback, useEffect, useRef, useState } from 'react';
import { searchRestaurants } from '../../services/api/restaurantService';
import { useAuth } from '../../auth/AuthContext';
import { useDeliveryAddress } from '../../address/DeliveryAddressContext';

const joinUniqueParts = (parts) => {
    const seen = new Set();
    return parts.filter((part) => {
        const normalized = String(part || '').trim();
        if (!normalized) return false;
        const key = normalized.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

const tryParseLatLng = (text) => {
    // Accept formats like "31.4812, 74.3039" or "31.4812 74.3039"
    const match = text.trim().match(/^\s*(-?\d{1,3}(?:\.\d+)?)\s*[, ]\s*(-?\d{1,3}(?:\.\d+)?)\s*$/);
    if (!match) return null;
    const lat = parseFloat(match[1]);
    const lon = parseFloat(match[2]);
    if (isNaN(lat) || isNaN(lon)) return null;
    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
    return { latitude: lat, longitude: lon };
};

const getGoogleAddressParts = (result) => {
    const parts = {
        streetNumber: '',
        route: '',
        premise: '',
        subpremise: '',
        suburb: '',
        city: '',
        county: '',
        state: '',
        postcode: '',
        country: '',
        countryCode: ''
    };

    (result?.address_components || []).forEach((component) => {
        const types = component.types || [];

        if (types.includes('street_number')) parts.streetNumber = component.long_name;
        if (types.includes('route')) parts.route = component.long_name;
        if (types.includes('premise')) parts.premise = component.long_name;
        if (types.includes('subpremise')) parts.subpremise = component.long_name;
        if (types.includes('sublocality') || types.includes('sublocality_level_1') || types.includes('neighborhood')) {
            parts.suburb = component.long_name;
        }
        if (types.includes('locality') || types.includes('postal_town')) parts.city = component.long_name;
        if (types.includes('administrative_area_level_2')) parts.county = component.long_name;
        if (types.includes('administrative_area_level_1')) parts.state = component.long_name;
        if (types.includes('postal_code')) parts.postcode = component.long_name;
        if (types.includes('country')) {
            parts.country = component.long_name;
            parts.countryCode = component.short_name.toLowerCase();
        }
    });

    return parts;
};

const formatGoogleResult = (result) => {
    const parts = getGoogleAddressParts(result);
    const streetLine = joinUniqueParts([
        parts.premise,
        [parts.streetNumber, parts.route].filter(Boolean).join(' ').trim(),
        parts.subpremise
    ]).join(', ');
    const localityLine = joinUniqueParts([parts.suburb, parts.city]).join(', ');
    const regionLine = joinUniqueParts([parts.county, parts.state, parts.postcode]).join(', ');
    const formatted = joinUniqueParts([streetLine, localityLine, regionLine, parts.country]).join(', ');

    return formatted || result?.formatted_address || '';
};

const pickBestGeocodeResult = (results = []) => {
    const preferredTypeOrder = [
        'street_address',
        'premise',
        'subpremise',
        'route',
        'establishment',
        'plus_code'
    ];

    for (const type of preferredTypeOrder) {
        const match = results.find((result) => result.types?.includes(type));
        if (match) return match;
    }

    return results[0] || null;
};

export default function Banner({ onLocationResolved }){
    const { customer } = useAuth();
    const { currentAddress } = useDeliveryAddress();
    const [deliveryAddress, setDeliveryAddress] = useState('');
    const [isSearchingAddress, setIsSearchingAddress] = useState(false);
    const [addressSuggestions, setAddressSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [userCoords, setUserCoords] = useState(null);
    const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [mapsError, setMapsError] = useState('');
    const suggestionsControllerRef = useRef(null);
    const googleMapsPromiseRef = useRef(null);

    const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    const ensureGoogleMaps = useCallback(() => {
        if (window.google?.maps?.Geocoder && window.google?.maps?.places?.AutocompleteService) {
            return Promise.resolve(window.google.maps);
        }

        if (!GOOGLE_MAPS_API_KEY) {
            console.error('Missing Google Maps API key. Set REACT_APP_GOOGLE_MAPS_API_KEY in your .env file.');
            setMapsError('Map search is not configured (missing Google Maps API key).');
            return Promise.reject(new Error('Missing REACT_APP_GOOGLE_MAPS_API_KEY'));
        }

        if (!googleMapsPromiseRef.current) {
            googleMapsPromiseRef.current = new Promise((resolve, reject) => {
                const existingScript = document.querySelector('script[data-google-maps-loader="true"]');

                if (existingScript) {
                    existingScript.addEventListener('load', () => resolve(window.google.maps), { once: true });
                    existingScript.addEventListener('error', () => reject(new Error('Failed to load Google Maps')), { once: true });
                    return;
                }

                const script = document.createElement('script');
                script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
                script.async = true;
                script.defer = true;
                script.dataset.googleMapsLoader = 'true';
                script.onload = () => resolve(window.google.maps);
                script.onerror = () => {
                    setMapsError('Failed to load Google Maps. Check network / API key / billing.');
                    reject(new Error('Failed to load Google Maps'));
                };
                document.head.appendChild(script);
            });
        }

        return googleMapsPromiseRef.current;
    }, [GOOGLE_MAPS_API_KEY]);

    const geocodeRequest = useCallback(async (request) => {
        const maps = await ensureGoogleMaps();
        const geocoder = new maps.Geocoder();

        return new Promise((resolve, reject) => {
            geocoder.geocode(request, (results, status) => {
                if (status === 'OK' && results?.length) {
                    resolve(results);
                    return;
                }

                reject(new Error(`Google geocode failed: ${status}`));
            });
        });
    }, [ensureGoogleMaps]);

    const reverseGeocodeToAddress = async ({ latitude, longitude }) => {
        const results = await geocodeRequest({
            location: { lat: latitude, lng: longitude }
        });
        const bestResult = pickBestGeocodeResult(results);
        return formatGoogleResult(bestResult) || `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
    };

    const resolveSearchResultToLocation = async (result) => {
        try {
            const results = await geocodeRequest(
                result?.place_id
                    ? { placeId: result.place_id }
                    : { address: result?.description || result?.formatted_address || '' }
            );
            const bestResult = pickBestGeocodeResult(results);
            const address = formatGoogleResult(bestResult);
            const loc = bestResult?.geometry?.location;
            const latitude = typeof loc?.lat === 'function' ? loc.lat() : undefined;
            const longitude = typeof loc?.lng === 'function' ? loc.lng() : undefined;
            return {
              address,
              coords:
                latitude != null && longitude != null
                  ? { latitude, longitude }
                  : null,
            };
        } catch (error) {
            const address = result?.label || result?.description || result?.formatted_address || '';
            return { address, coords: null };
        }
    };

    const handleSuggestionSelection = async (item) => {
        setIsSearchingAddress(true);
        try {
            // Backend searchRestaurants fallback suggestions carry a branch address already.
            if (item?.__branch?.address) {
                const address = String(item.__branch.address);
                const latitude = item?.__branch?.latitude ? parseFloat(item.__branch.latitude) : NaN;
                const longitude = item?.__branch?.longitude ? parseFloat(item.__branch.longitude) : NaN;
                const coords = Number.isFinite(latitude) && Number.isFinite(longitude)
                  ? { latitude, longitude }
                  : null;

                setDeliveryAddress(address);
                setShowSuggestions(false);
                setAddressSuggestions([]);
                setHighlightedIndex(-1);
                if (typeof onLocationResolved === 'function') {
                  onLocationResolved({ address, coords });
                }
                return;
            }

            const resolved = await resolveSearchResultToLocation(item);
            const address = String(resolved?.address || '').trim();
            if (address) {
                setDeliveryAddress(address);
                if (typeof onLocationResolved === 'function') {
                  onLocationResolved({ address, coords: resolved?.coords || null });
                }
            }
            setShowSuggestions(false);
            setAddressSuggestions([]);
            setHighlightedIndex(-1);
        } finally {
            setIsSearchingAddress(false);
        }
    };

    const searchAddressFromText = async (searchText) => {
        const trimmedText = searchText.trim();
        if (!trimmedText) return;

        // If user typed coordinates, reverse geocode directly
        const coords = tryParseLatLng(trimmedText);
        if (coords) {
            setIsSearchingAddress(true);
            try {
                const formatted = await reverseGeocodeToAddress(coords);
                setDeliveryAddress(formatted);
                setShowSuggestions(false);
            } finally {
                setIsSearchingAddress(false);
            }
            return;
        }

        setIsSearchingAddress(true);
        try {
            const results = await geocodeRequest({
                address: trimmedText,
            });
            const first = pickBestGeocodeResult(results);
            if (first) {
                const formatted = formatGoogleResult(first);
                setDeliveryAddress(formatted);
                setShowSuggestions(false);
            }
        } catch (error) {
            // Keep user-entered value if search fails.
        } finally {
            setIsSearchingAddress(false);
        }
    };

    useEffect(() => {
        const trimmedQuery = deliveryAddress.trim();
        if (!trimmedQuery) {
            setAddressSuggestions([]);
            return;
        }

        // Only query when user has typed at least 3 characters (unless it's coordinates)
        const coords = tryParseLatLng(trimmedQuery);
        if (!coords && trimmedQuery.length < 3) {
            setAddressSuggestions([]);
            return;
        }

        const timeoutId = window.setTimeout(async () => {
            const requestId = (suggestionsControllerRef.current || 0) + 1;
            suggestionsControllerRef.current = requestId;
            setIsLoadingSuggestions(true);
            try {
                // If Google Maps isn't configured/available, fallback to backend searchRestaurants.
                if (!GOOGLE_MAPS_API_KEY || mapsError) {
                    const { restaurants } = await searchRestaurants(trimmedQuery);
                    if (suggestionsControllerRef.current !== requestId) return;

                    const suggestions = (restaurants || []).slice(0, 8).map((r) => {
                        const b = Array.isArray(r?.branches) ? r.branches[0] : null;
                        const address = b?.address ? String(b.address) : '';
                        return {
                            place_id: `restaurant:${r?.id || r?.name || address}`,
                            label: [r?.name, address].filter(Boolean).join(' — '),
                            description: address || r?.name || '',
                            __restaurant: r,
                            __branch: b,
                        };
                    }).filter((s) => String(s.label || '').trim());

                    setAddressSuggestions(suggestions);
                    return;
                }

                const maps = await ensureGoogleMaps();
                const service = new maps.places.AutocompleteService();
                const looksNumericOnly = /^\d+$/.test(trimmedQuery);
                const predictions = await new Promise((resolve, reject) => {
                    service.getPlacePredictions({
                        input: trimmedQuery,
                        // Global search: do not restrict to a country.
                        // Using geocode type improves results for postcodes / street numbers.
                        types: ['geocode'],
                        ...(userCoords?.latitude != null && userCoords?.longitude != null
                            ? { location: new maps.LatLng(userCoords.latitude, userCoords.longitude), radius: 50000 }
                            : {})
                    }, (results, status) => {
                        if (status === maps.places.PlacesServiceStatus.OK || status === 'OK') {
                            resolve(results || []);
                            return;
                        }

                        if (status === maps.places.PlacesServiceStatus.ZERO_RESULTS || status === 'ZERO_RESULTS') {
                            resolve([]);
                            return;
                        }

                        reject(new Error(`Google autocomplete failed: ${status}`));
                    });
                });
                if (suggestionsControllerRef.current !== requestId) {
                    return;
                }
                let withLabels = (predictions || []).map((prediction) => ({
                    ...prediction,
                    label: prediction.description
                }));

                // Numeric searches (ZIP / house numbers) often return ZERO_RESULTS from Autocomplete.
                // Fallback to Geocoder for global numeric lookups.
                if (!withLabels.length && looksNumericOnly) {
                    try {
                        const geoResults = await geocodeRequest({ address: trimmedQuery });
                        const best = pickBestGeocodeResult(geoResults);
                        if (best) {
                            withLabels = [{
                                place_id: best.place_id || `geocode:${trimmedQuery}`,
                                description: best.formatted_address || trimmedQuery,
                                label: formatGoogleResult(best) || best.formatted_address || trimmedQuery,
                            }];
                        }
                    } catch (e) {
                        // ignore
                    }
                }

                const seen = new Set();
                const deduped = withLabels.filter((d) => {
                    const key = (d.label || '').toLowerCase();
                    if (seen.has(key)) return false;
                    seen.add(key);
                    return true;
                });
                setAddressSuggestions(deduped);
            } catch (error) {
                if (suggestionsControllerRef.current === requestId) {
                    setAddressSuggestions([]);
                }
            } finally {
                if (suggestionsControllerRef.current === requestId) {
                    setIsLoadingSuggestions(false);
                }
            }
        }, 350);

        return () => window.clearTimeout(timeoutId);
    }, [deliveryAddress, userCoords, GOOGLE_MAPS_API_KEY, mapsError, ensureGoogleMaps, geocodeRequest]);

    useEffect(() => {
        const lat = currentAddress?.coords?.latitude;
        const lng = currentAddress?.coords?.longitude;
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
            setUserCoords({ latitude: lat, longitude: lng });
        }
    }, [currentAddress?.coords?.latitude, currentAddress?.coords?.longitude]);

    useEffect(() => {
        const addr = String(currentAddress?.address || '').trim();
        if (!addr) return;
        setDeliveryAddress((prev) => (String(prev || '').trim() ? prev : addr));
    }, [currentAddress?.address]);

    return <>
        <div className='banner'>
                <div className='container'>
                    <div className='banner_row d-flex justify-content-between'>
                        <div>
                            <img src={left_gif} alt="" className='banner_left_gif' />
                        </div>

                        <div className='banner_delivery_div'>
                            <div className='banner_input_div'>
                            <input
                                type='text'
                                className='banner_delivery_text_box'
                                placeholder='Enter Delivery Address'
                                value={deliveryAddress}
                                onChange={(e) => {
                                    setDeliveryAddress(e.target.value);
                                    setShowSuggestions(true);
                                    setHighlightedIndex(-1);
                                    if (mapsError) setMapsError('');
                                }}
                                onFocus={() => setShowSuggestions(true)}
                                onBlur={() => {
                                    // Delay close so suggestion click can register.
                                    window.setTimeout(() => setShowSuggestions(false), 120);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !isSearchingAddress) {
                                        e.preventDefault();
                                        if (showSuggestions && highlightedIndex >= 0 && highlightedIndex < addressSuggestions.length) {
                                            const chosen = addressSuggestions[highlightedIndex];
                                            void handleSuggestionSelection(chosen);
                                        } else {
                                            void searchAddressFromText(deliveryAddress);
                                        }
                                    } else if (e.key === 'ArrowDown' && showSuggestions) {
                                        e.preventDefault();
                                        setHighlightedIndex((i) => Math.min(i + 1, addressSuggestions.length - 1));
                                    } else if (e.key === 'ArrowUp' && showSuggestions) {
                                        e.preventDefault();
                                        setHighlightedIndex((i) => Math.max(i - 1, 0));
                                    } else if (e.key === 'Escape') {
                                        setShowSuggestions(false);
                                    }
                                }}
                            />
                            {mapsError ? (
                                <div className="small text-danger mt-1" role="alert">
                                    {mapsError}
                                </div>
                            ) : null}
                            {showSuggestions && addressSuggestions.length > 0 && (
                                <div className='banner_suggestions'>
                                    {addressSuggestions.map((item, idx) => (
                                        <button
                                            key={item.place_id}
                                            type='button'
                                            className={`banner_suggestion_item ${idx === highlightedIndex ? 'banner_suggestion_item--active' : ''}`}
                                            onMouseDown={() => {
                                                void handleSuggestionSelection(item);
                                            }}
                                            onMouseEnter={() => setHighlightedIndex(idx)}
                                        >
                                            {(() => {
                                                const label = item.label || item.display_name || '';
                                                const q = deliveryAddress.trim();
                                                const i = label.toLowerCase().indexOf(q.toLowerCase());
                                                if (q && i >= 0) {
                                                    const before = label.slice(0, i);
                                                    const match = label.slice(i, i + q.length);
                                                    const after = label.slice(i + q.length);
                                                    return (
                                                        <>
                                                            <span>{before}</span>
                                                            <mark>{match}</mark>
                                                            <span>{after}</span>
                                                        </>
                                                    );
                                                }
                                                return label;
                                            })()}
                                        </button>
                                    ))}
                                </div>
                            )}
                            {showSuggestions && !isLoadingSuggestions && addressSuggestions.length === 0 && (
                                <div className='banner_suggestions banner_suggestions--empty'>
                                    <div className='banner_suggestion_empty'>No results</div>
                                </div>
                            )}
                            {showSuggestions && isLoadingSuggestions && (
                                <div className='banner_suggestions banner_suggestions--loading'>
                                    <div className='banner_suggestion_loading'>Searching…</div>
                                </div>
                            )}
                            </div>
                            <Heading level={2} className='banner_heading_white'>
                              SO <span className='banner_heading_orange'>HAPPY</span> TO HAVE <br />
                              YOU{customer?.name && String(customer.name).trim() ? (
                                <>
                                  {' '}
                                  <span className='banner_heading_orange'>{String(customer.name).trim()}!</span>
                                </>
                              ) : (
                                '!'
                              )}
                            </Heading>
                        </div>

                        <div>
                            <img src={right_gif} alt="" className='banner_right_gif' />
                        </div>
                    </div>
                </div>
        </div>
    </>

}